<!-- ══════════════════════════════════════════════════════
     terms-of-service.component.html
     ══════════════════════════════════════════════════════ -->

<div class="tos-layout">

  <!-- ── Sidebar ─────────────────────────────────────────── -->
  <aside class="tos-sidebar">
    <div class="tos-sidebar__label">Sections</div>
    <nav>
      <a routerLink="./" fragment="acceptance-of-terms">1. Acceptance of Terms</a>
      <a routerLink="./" fragment="description-of-services">2. Description of Services</a>
      <a routerLink="./" fragment="brokerage-relationship">3. Brokerage Relationship</a>
      <a routerLink="./" fragment="user-accounts-registration">4. User Accounts</a>
      <a routerLink="./" fragment="fees-subscriptions-payment">5. Fees &amp; Payment</a>
      <a routerLink="./" fragment="contract-builder-terms">6. Contract Builder</a>
      <a routerLink="./" fragment="acceptable-use-prohibited-conduct">7. Acceptable Use</a>
      <a routerLink="./" fragment="intellectual-property">8. Intellectual Property</a>
      <a routerLink="./" fragment="privacy-data">9. Privacy &amp; Data</a>
      <a routerLink="./" fragment="disclaimer-of-warranties">10. Disclaimer of Warranties</a>
      <a routerLink="./" fragment="limitation-of-liability">11. Limitation of Liability</a>
      <a routerLink="./" fragment="indemnification">12. Indemnification</a>
      <a routerLink="./" fragment="dispute-resolution-arbitration">13. Dispute Resolution</a>
      <a routerLink="./" fragment="governing-law-jurisdiction">14. Governing Law</a>
      <a routerLink="./" fragment="termination">15. Termination</a>
      <a routerLink="./" fragment="general-provisions">16. General Provisions</a>
      <a routerLink="./" fragment="contact-information">17. Contact Us</a>
    </nav>
  </aside>

  <!-- ── Main ────────────────────────────────────────────── -->
  <main class="tos-main">
    <!-- Hero -->
    <header class="tos-hero">
      <div class="tos-hero__eyebrow">Legal Document</div>
      <h1 class="tos-hero__title">Terms of Service</h1>
      <div class="tos-hero__meta">
        <strong>HomeShark, Inc.</strong>&nbsp;·&nbsp;homeshark.com&nbsp;·&nbsp;Effective Date: March 9, 2026
      </div>
    </header>

    <!-- Content -->
    <div class="tos-content">

      <!-- Quick Reference -->
      <div class="tos-alert tos-alert--info">
        <div class="tos-alert__title">Quick Reference</div>
        <p>Company: HomeShark, Inc.&nbsp;·&nbsp;Jurisdiction: State of Georgia&nbsp;·&nbsp;Governing Law: Georgia&nbsp;·&nbsp;Arbitration:
          Yes&nbsp;·&nbsp;GA Broker License: 272410&nbsp;·&nbsp;Last Updated: March 9, 2026</p>
      </div>

      <!-- 1. Acceptance of Terms -->
      <h2 class="tos-h1" id="acceptance-of-terms">1. Acceptance of Terms</h2>
      <p>Welcome to HomeShark. These Terms of Service (<strong>"Terms"</strong>) constitute a legally binding agreement
        between you, a registered user of HomeShark (hereinafter, <strong>"User," "Registered User," "you,"</strong> or
        <strong>"your"</strong>) and HomeShark, Inc., a Georgia corporation (hereinafter, <strong>"HomeShark,"
          "Company," "we," "us,"</strong> or <strong>"our"</strong>), operating the real estate technology platform
        accessible at homeshark.com, and related mobile applications and desktop applications (collectively, the
        <strong>"Platform"</strong>).</p>

      <div class="tos-alert tos-alert--warning">
        <div class="tos-alert__title">By Accessing or Using the Platform</div>
        <p>BY ACCESSING OR USING THE PLATFORM IN ANY WAY, YOU ACKNOWLEDGE THAT YOU HAVE READ, UNDERSTOOD, AND AGREE TO
          BE BOUND BY THESE TERMS. If you do not agree to these Terms, you must immediately discontinue all use of the
          Platform.</p>
      </div>

      <p>These Terms apply to all unauthenticated visitors, registered users, and any other persons who access or use
        the Platform, regardless of whether they have created an account.</p>
      <p>We reserve the right to modify these Terms at any time. We will provide notice of material changes by posting
        the revised Terms on the Platform with an updated effective date. Your continued use of the Platform after any
        such changes constitutes your acceptance of the new Terms. If you do not agree to the revised Terms, you must
        discontinue your use of the Platform.</p>

      <!-- 2. Description of Services -->
      <h2 class="tos-h1" id="description-of-services">2. Description of Services</h2>
      <p>HomeShark operates a real estate technology platform that offers the following services to Users:</p>

      <h3 class="tos-h2" id="mls-property-search">2.1 MLS Property Search</h3>
      <p>HomeShark imports third-party MLS data into the Platform. The Platform provides access to residential property
        listings sourced from the First Multiple Listing Service (FMLS) and Georgia Multiple Listing Service (GAMLS)
        (collectively, the <strong>"MLS"</strong>) through licensed data feeds. HomeShark itself does not generate,
        review, edit or independently provide residential property listing data. Listing data is provided for
        informational purposes only and is subject to the terms and display rules of each MLS. HomeShark makes no
        representation regarding a listed property's marketability of title, nor the quality, completeness, accuracy, or
        timeliness of any property or associated listing data.</p>

      <h3 class="tos-h2" id="showing-coordination">2.2 Showing Coordination</h3>
      <p>HomeShark provides a technology-enabled communication tool that allows Users to submit requests to schedule
        in-person showings of residential properties listed on the MLS. HomeShark's role in this process is strictly
        limited to facilitating the transmission of scheduling requests and related communications between Users and
        listing agents or their designated representatives.</p>
      <p>HomeShark does not select properties for you, recommend properties, provide advice regarding showings, or act
        on your behalf in a representative or fiduciary capacity in connection with any showing request prior to the
        establishment of a formal brokerage relationship as described in Section 3.</p>
      <p>All showing availability, confirmations, and scheduling details are determined solely by listing agents,
        sellers, or third-party scheduling systems. HomeShark does not control and does not guarantee the availability
        of any property for showing, the timing of any showing, or the accuracy of any scheduling-related
        information.</p>
      <p>Additionally, no agency, fiduciary, or brokerage relationship is created by coordinating showings through this
        pipeline.</p>

      <h3 class="tos-h2" id="contract-builder">2.3 Contract Builder</h3>
      <p>The Contract Builder is a user-directed technology tool that enables registered Users to input information and
        selections in order to generate a draft Georgia Residential Purchase and Sale Agreement based on those
        inputs.</p>
      <p>The Contract Builder operates solely as a formatting and document assembly tool. The Contract Builder is
        user-directed only and we do not select terms for you. The tool is used only for the purpose of creating the
        terms of an offer via a formatting mechanism based on user input. HomeShark does not select contract terms,
        recommend legal provisions, or provide legal advice through the Contract Builder.</p>
      <p><strong>Broker representation is provided exclusively in connection with and upon initiation of the Contract
        Builder workflow</strong> and your electronic execution of a Georgia Buyer Brokerage Agreement presented through
        the Platform. <strong>No agency, fiduciary, or brokerage relationship is created by any preliminary
          communications, showing coordination, or assistance prior to execution of the Georgia Buyer Brokerage
          Agreement.</strong> Prior to initiation of the Contract Builder and execution of a Georgia Buyer Brokerage
        Agreement, HomeShark operates solely as a technology platform and does not provide real estate brokerage
        services. Even after execution of the agreement, there is no legal review or legal representation by HomeShark
        or associated brokerage.</p>
      <p>The Contract Builder is not a substitute for legal counsel. Users are solely responsible for reviewing any
        generated document and are strongly encouraged to seek independent legal advice prior to execution.</p>

      <h3 class="tos-h2" id="user-responsibility-services">2.4 User Responsibility</h3>
      <p>You are solely responsible for the accuracy, completeness, and legal sufficiency of all information and
        selections you provide through the Contract Builder. HomeShark does not independently verify any information
        entered by you. HomeShark does not guarantee that any generated document complies with all applicable legal
        requirements or will be accepted in any specific transaction.</p>
      <p>You acknowledge that you may be creating your own legally binding documents that may have significant financial
        and legal consequences. You assume all risks associated with the use of any contract generated through the
        Platform.</p>

      <h3 class="tos-h2" id="platform-limitations">2.5 Platform Limitations</h3>
      <p>As of the date of these Terms, HomeShark operates exclusively within the State of Georgia. The Platform, MLS
        data, Contract Builder, and all related services are intended for use in connection with real property located
        in Georgia only. Use of the Platform in connection with properties located outside of Georgia is not supported
        and may produce inaccurate or inapplicable results.</p>

      <div class="tos-alert tos-alert--info">
        <div class="tos-alert__title">Important: Not Legal or Financial Advice</div>
        <p>Nothing on the Platform constitutes legal advice, financial advice, tax advice, or investment advice. All
          property information, valuations, estimates, and market data are provided for general informational purposes
          only. You should consult qualified legal, financial, and real estate professionals before making any real
          estate decision.</p>
      </div>

      <!-- 3. Brokerage -->
      <h2 class="tos-h1" id="brokerage-relationship">3. Real Estate Brokerage Relationship &amp; Disclosures</h2>

      <h3 class="tos-h2" id="platform-use-vs-brokerage">3.1 Platform Use vs. Brokerage Services</h3>
      <p>HomeShark operates a real estate technology platform that is made available in connection with a licensed
        Georgia real estate brokerage. However, your use of the Platform — including browsing listings, saving
        properties, receiving automated property information, or submitting showing requests — does not, by itself,
        create any agency, fiduciary, or brokerage relationship between you and HomeShark or its affiliated broker.</p>
      <p>Prior to the execution of a written Buyer Brokerage Agreement, you are considered an unrepresented user of a
        technology platform. During this period:</p>
      <ul>
        <li><p>HomeShark does not act as your agent or representative.</p></li>
        <li><p>HomeShark does not owe you fiduciary duties.</p></li>
        <li><p>HomeShark does not provide real estate advice, negotiation services, or representation.</p></li>
        <li><p>Any assistance provided is administrative or ministerial in nature.</p></li>
      </ul>
      <p>A brokerage relationship is created only upon (a) your affirmative initiation of the Contract Builder workflow
        and (b) your execution of a written Georgia Buyer Brokerage Agreement presented through the Platform.</p>
      <p>No oral statements, preliminary communications, or Platform interactions shall be deemed to create a brokerage
        relationship absent a fully executed written agreement.</p>

      <h3 class="tos-h2" id="buyer-brokerage-disclosure">3.2 Georgia Buyer Brokerage Disclosure</h3>
      <p>In accordance with the requirements of the Georgia Real Estate Commission (GREC) and O.C.G.A. Title 43, Chapter
        40, HomeShark will present you with a Buyer Brokerage Disclosure and, upon initiation of the Contract Builder, a
        Buyer Brokerage Agreement. You must review and execute these documents before any brokerage services are
        rendered on your behalf.</p>

      <h3 class="tos-h2" id="mls-data-idx-compliance">3.3 MLS Data &amp; IDX Compliance</h3>
      <p>Listing information displayed on the Platform is sourced from FMLS and GAMLS pursuant to Internet Data Exchange
        (IDX) and Virtual Office Website (VOW) data licensing agreements. Information is deemed reliable, but is not
        guaranteed. All listing content remains the property of the respective MLS and the listing brokerage as
        identified and displayed. HomeShark does not have knowledge of and is not responsible for any errors, omissions
        or misrepresentations in MLS data. Users may not copy, reproduce, redistribute, or commercially exploit any MLS
        listing data obtained through the Platform.</p>

      <h3 class="tos-h2" id="no-guarantee-outcome">3.4 No Guarantee of Transaction Outcome</h3>
      <p>HomeShark does not guarantee that use of the Platform will result in a completed real estate transaction, an
        accepted offer, a successful showing or showings, or any other outcome. Real estate transactions are subject to
        market conditions, seller decisions, lender requirements, and other factors outside of HomeShark's control.</p>

      <h3 class="tos-h2" id="transaction-parties">3.5 Real Estate Transaction Parties</h3>
      <p>When you initiate the Contract Builder, engage in a brokerage relationship by your electronic execution of a
        Georgia Buyer Brokerage Agreement and authorize HomeShark to submit an offer on your behalf, we will share the
        relevant contract and contact information with the listing agent and/or seller as required to present your
        offer. This sharing is a core part of the brokerage services you have engaged HomeShark to provide and is not a
        sale of your data. Please see the Privacy Policy, incorporated herein by reference, for more information.</p>

      <!-- 4. User Accounts -->
      <h2 class="tos-h1" id="user-accounts-registration">4. User Accounts &amp; Registration</h2>

      <h3 class="tos-h2" id="eligibility">4.1 Eligibility</h3>
      <p>You must be at least eighteen (18) years of age and legally capable of entering into binding contracts under
        applicable law to register for a HomeShark account or to use any features of the Platform. By registering, you
        represent and warrant that you meet these requirements.</p>

      <h3 class="tos-h2" id="account-registration">4.2 Account Registration</h3>
      <p>To access certain features of the Platform — including saving favorite properties, requesting showings, and
        using the Contract Builder — you must create a registered account. You agree to provide accurate, current, and
        complete information during registration and to keep your account information up to date.</p>

      <h3 class="tos-h2" id="account-security">4.3 Account Security</h3>
      <p>You are solely responsible for maintaining the confidentiality of your account credentials and for all activity
        that occurs under your account. You agree to notify HomeShark immediately of any unauthorized use of your
        account or any other security breach. HomeShark is not liable for any loss or damage arising from your failure
        to comply with this section.</p>

      <h3 class="tos-h2" id="one-account">4.4 One Account Per User</h3>
      <p>Each User may maintain only one active account. Creating multiple accounts to circumvent account restrictions,
        suspensions, or other limitations is prohibited and may result in permanent termination of all associated
        accounts.</p>

      <h3 class="tos-h2" id="account-verification">4.5 Account Verification</h3>
      <p>HomeShark may require you to verify your identity or contact information (by email or SMS) as a condition of
        account activation or continued use. Failure to complete required verification may result in limited or
        suspended access to Platform features.</p>

      <!-- 5. Fees -->
      <h2 class="tos-h1" id="fees-subscriptions-payment">5. Fees, Subscriptions &amp; Payment</h2>

      <h3 class="tos-h2" id="fee-based-services">5.1 Fee-Based Services</h3>
      <p>HomeShark offers fee-based access to certain Platform features through the following arrangements:</p>
      <ul>
        <li><p><strong>Subscription Plans:</strong> Monthly or annual subscription fees for full access to Platform
          features including advanced MLS search, saved searches, and Contract Builder access.</p></li>
        <li><p><strong>Flat-Fee Transaction Services:</strong> A one-time fee per real estate transaction for brokerage
          services rendered in connection with the Contract Builder workflow.</p></li>
        <li><p><strong>Commission-Based Services:</strong> Brokerage compensation through a buyer-side commission earned
          at closing, as disclosed in the applicable Buyer Brokerage Agreement.</p></li>
      </ul>

      <h3 class="tos-h2" id="billing-payment">5.2 Billing &amp; Payment</h3>
      <p>All subscription fees are billed in advance on a monthly or annual basis, as selected at the time of
        enrollment. Fees are non-refundable except as expressly stated in these Terms or required by applicable law.
        HomeShark uses third-party payment processors to handle payment transactions. By providing payment information,
        you authorize HomeShark and its payment processor to charge the applicable fees to your designated payment
        method.</p>

      <h3 class="tos-h2" id="auto-renewal">5.3 Subscription Auto-Renewal</h3>
      <p>Subscriptions automatically renew at the end of each billing period unless you cancel your subscription prior
        to the renewal date. You may cancel your subscription at any time through your account settings or by contacting
        HomeShark support. Cancellation takes effect at the end of the current billing period; no partial refunds are
        provided for unused subscription time.</p>

      <h3 class="tos-h2" id="fee-changes">5.4 Fee Changes</h3>
      <p>HomeShark reserves the right to change subscription fees or introduce new fees at any time with at least thirty
        (30) days' advance notice. Continued use of the Platform after the effective date of any fee change constitutes
        your acceptance of the revised fees.</p>

      <h3 class="tos-h2" id="taxes">5.5 Taxes</h3>
      <p>You are responsible for all applicable taxes, fees, and charges imposed by governmental authorities in
        connection with your use of the Platform or any real estate transaction facilitated through the Platform, except
        to the extent HomeShark is required by law to collect and remit such taxes.</p>

      <!-- 6. Contract Builder -->
      <h2 class="tos-h1" id="contract-builder-terms">6. Contract Builder — Specific Terms</h2>

      <h3 class="tos-h2" id="nature-of-contract-builder">6.1 Nature of the Contract Builder</h3>
      <p>The Contract Builder is a guided technology tool designed to assist registered Users in preparing a draft
        Georgia Residential Purchase and Sale Agreement. The tool is not a substitute for legal counsel. The contract
        generated is a document that creates binding legal obligations upon execution.</p>

      <h3 class="tos-h2" id="broker-review">6.2 Broker Review Requirement</h3>
      <p><strong>All agreements generated through the Contract Builder are subject to mandatory review and approval by
        the HomeShark supervising broker before submission to any listing agent or seller.</strong> No contract
        generated through the Platform will be submitted on your behalf without prior broker review and approval. Broker
        review is limited ONLY to brokerage compliance and does not constitute legal review, verification of accuracy,
        or guarantee of enforceability of any documentation or terms. HomeShark will endeavor to complete broker review
        within one (1) business day of agreement submission, but does not guarantee any specific review timeline.</p>

      <h3 class="tos-h2" id="user-responsibility-contract">6.3 User Responsibility</h3>
      <p>You are responsible for the accuracy and completeness of all information you provide through the Contract
        Builder. HomeShark and its broker are not responsible for errors, omissions, or adverse outcomes resulting from
        inaccurate or incomplete information provided by you. You acknowledge that real estate contracts are complex
        legal instruments and that you have been advised to seek independent legal counsel.</p>

      <h3 class="tos-h2" id="gar-form-compliance">6.4 GAR Form Compliance</h3>
      <p>HomeShark intends for contract templates used in the Contract Builder to substantially conform to the current
        Georgia Association of REALTORS (GAR) standard residential purchase and sale agreement form requirements.
        However, HomeShark makes no warranty that any generated contract will be accepted by any seller, listing agent,
        or court, or that it will satisfy all legal requirements applicable to your specific transaction.</p>

      <h3 class="tos-h2" id="electronic-signatures">6.5 Electronic Signatures</h3>
      <p>The Platform may use third-party electronic signature services to facilitate execution of contracts and
        brokerage agreements. Electronic signatures applied through the Platform are intended to have the same legal
        effect as handwritten signatures pursuant to the Georgia Electronic Records and Signatures Act (O.C.G.A. Title
        10, Chapter 12) and the federal Electronic Signatures in Global and National Commerce Act (E-SIGN). You consent
        to the use of electronic records and signatures for all Platform transactions.</p>

      <!-- 7. Acceptable Use -->
      <h2 class="tos-h1" id="acceptable-use-prohibited-conduct">7. Acceptable Use &amp; Prohibited Conduct</h2>
      <p>You agree to use the Platform only for lawful purposes and in accordance with these Terms. You agree not
        to:</p>
      <ul>
        <li><p>Use the Platform for any purpose that violates any applicable federal, state, or local law or
          regulation.</p></li>
        <li><p>Access, collect, scrape, harvest, or otherwise extract any MLS listing data, property information, or
          other Platform content through automated means, bots, scrapers, crawlers, or any technology other than the
          Platform's standard user interface.</p></li>
        <li><p>Reproduce, distribute, modify, create derivative works from, publicly display, or commercially exploit
          any content obtained from the Platform, including MLS listing data, contract templates, or proprietary
          Platform features.</p></li>
        <li><p>Attempt to gain unauthorized access to any portion of the Platform, other user accounts, or any systems
          or networks connected to the Platform.</p></li>
        <li><p>Interfere with, disrupt, or damage the integrity or performance of the Platform or the data contained
          therein.</p></li>
        <li><p>Impersonate any person or entity, or misrepresent your affiliation with any person or entity, in
          connection with your use of the Platform.</p></li>
        <li><p>Use the Platform to transmit unsolicited commercial communications, spam, or any content that is harmful,
          fraudulent, deceptive, threatening, harassing, defamatory, or otherwise objectionable.</p></li>
        <li><p>Attempt to reverse engineer, decompile, disassemble, or otherwise derive the source code of the
          Platform.</p></li>
        <li><p>Use the Platform in connection with any real property located outside the State of Georgia.</p></li>
        <li><p>Create multiple accounts or share your account credentials with third parties.</p></li>
      </ul>
      <p>HomeShark reserves the right to investigate and take appropriate legal action against any User who, in
        HomeShark's sole discretion, violates this Section, including reporting such activity to law enforcement
        authorities.</p>

      <!-- 8. IP -->
      <h2 class="tos-h1" id="intellectual-property">8. Intellectual Property</h2>

      <h3 class="tos-h2" id="homeshark-ip">8.1 HomeShark Intellectual Property</h3>
      <p>The Platform and all of its content, features, and functionality — including but not limited to software,
        algorithms, text, graphics, logos, icons, images, audio clips, data compilations, contract templates, and the
        selection and arrangement thereof — are owned by HomeShark, its licensors, or other providers of such material,
        and are protected by United States and international intellectual property laws. The <strong>HomeShark</strong>
        name, logo, and all related names, logos, product and service names, designs, and slogans are trademarks of
        HomeShark, Inc. You may not use such marks without the prior written permission of HomeShark.</p>

      <h3 class="tos-h2" id="mls-content-ownership">8.2 MLS Content Ownership</h3>
      <p>All MLS listing content, including property descriptions, photographs, and listing data, remains the property
        of the respective MLS organizations (FMLS and GAMLS) and their member brokerages. Such content is licensed to
        HomeShark for display purposes only and is subject to MLS data licensing terms. You acquire no ownership rights
        in MLS content by accessing it through the Platform.</p>

      <h3 class="tos-h2" id="limited-license">8.3 Limited License to Users</h3>
      <p>Subject to your compliance with these Terms, HomeShark grants you a limited, non-exclusive, non-transferable,
        revocable license to access and use the Platform and its content solely for your personal, non-commercial
        homebuying purposes. This license does not include any right to: collect or harvest any data from the Platform;
        use data mining, robots, or similar data gathering tools; copy or reproduce the Platform or its content for
        commercial use; or frame or mirror any part of the Platform without HomeShark's prior written authorization.</p>

      <h3 class="tos-h2" id="user-content">8.4 User Content</h3>
      <p>By submitting any content, information, or data to the Platform (including contract information, preferences,
        and communications), you grant HomeShark a non-exclusive, worldwide, royalty-free license to use, store,
        display, and process such content solely as necessary to provide the Platform services to you. HomeShark will
        not sell your personal content or data to third parties for marketing purposes.</p>

      <!-- 9. Privacy -->
      <h2 class="tos-h1" id="privacy-data">9. Privacy &amp; Data</h2>
      <p>Your use of the Platform is subject to HomeShark's Privacy Policy, which is incorporated into these Terms by
        reference. By using the Platform, you consent to the collection, use, and sharing of your information as
        described in the Privacy Policy. The Privacy Policy is available at <strong>homeshark.com/privacy</strong>.</p>
      <p>HomeShark collects and processes personal information including your name, contact information, account
        credentials, property search activity, and transaction information. This information is used to provide and
        improve the Platform, facilitate real estate transactions, communicate with you, and comply with legal
        obligations.</p>
      <p>HomeShark implements industry-standard security measures to protect your personal information, including
        encryption of data in transit and at rest. However, no method of transmission over the internet or electronic
        storage is completely secure, and HomeShark cannot guarantee the absolute security of your information.</p>

      <!-- 10. Disclaimer -->
      <h2 class="tos-h1" id="disclaimer-of-warranties">10. Disclaimer of Warranties</h2>

      <div class="tos-alert tos-alert--warning">
        <div class="tos-alert__title">Important — Please Read Carefully</div>
        <p>THE PLATFORM AND ALL CONTENT, SERVICES, AND FEATURES ARE PROVIDED ON AN "AS IS" AND "AS AVAILABLE" BASIS
          WITHOUT WARRANTIES OF ANY KIND, EITHER EXPRESS OR IMPLIED. TO THE FULLEST EXTENT PERMITTED BY APPLICABLE LAW,
          HOMESHARK EXPRESSLY DISCLAIMS ALL WARRANTIES, EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO IMPLIED
          WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, TITLE, AND NON-INFRINGEMENT.</p>
      </div>

      <p>Without limiting the foregoing, HomeShark makes no warranty that:</p>
      <ul>
        <li><p>The Platform will meet your requirements or be available on an uninterrupted, timely, secure, or
          error-free basis.</p></li>
        <li><p>Any MLS listing data displayed on the Platform is accurate, complete, current, or free from errors or
          omissions.</p></li>
        <li><p>Any contract generated through the Contract Builder will be legally sufficient, accepted by any seller or
          listing agent, or result in a completed transaction.</p></li>
        <li><p>The results obtained from use of the Platform will be accurate, reliable, or meet your expectations.</p>
        </li>
        <li><p>Any defects or errors in the Platform will be corrected.</p></li>
      </ul>
      <p>All MLS listing information is provided by third-party MLS sources and HomeShark does not independently verify
        listing data. You should independently verify all property information before making any real estate
        decision.</p>

      <!-- 11. Limitation of Liability -->
      <h2 class="tos-h1" id="limitation-of-liability">11. Limitation of Liability</h2>

      <div class="tos-alert tos-alert--warning">
        <div class="tos-alert__title">Limitation of Liability — Please Read Carefully</div>
        <p>TO THE MAXIMUM EXTENT PERMITTED BY APPLICABLE GEORGIA LAW, IN NO EVENT SHALL HOMESHARK, ITS OFFICERS,
          DIRECTORS, EMPLOYEES, AGENTS, LICENSORS, OR SERVICE PROVIDERS BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL,
          CONSEQUENTIAL, PUNITIVE, OR EXEMPLARY DAMAGES, INCLUDING BUT NOT LIMITED TO LOSS OF PROFITS, LOSS OF DATA,
          LOSS OF GOODWILL, OR OTHER INTANGIBLE LOSSES, ARISING OUT OF OR RELATING TO YOUR USE OF OR INABILITY TO USE
          THE PLATFORM, EVEN IF HOMESHARK HAS BEEN ADVISED OF THE POSSIBILITY OF SUCH DAMAGES.</p>
      </div>

      <p>In no event shall HomeShark's total cumulative liability to you for all claims arising out of or relating to
        these Terms or your use of the Platform exceed the greater of: (a) the total fees paid by you to HomeShark
        during the twelve (12) months immediately preceding the event giving rise to the claim; or (b) one hundred
        dollars ($100.00).</p>
      <p>Some jurisdictions do not allow the exclusion or limitation of certain damages, so the above limitations may
        not apply to you to the extent prohibited by applicable law.</p>
      <p>The foregoing limitations of liability are a fundamental element of the basis of the bargain between HomeShark
        and you. HomeShark would not be able to provide the Platform on an economically feasible basis without these
        limitations.</p>

      <!-- 12. Indemnification -->
      <h2 class="tos-h1" id="indemnification">12. Indemnification</h2>
      <p>You agree to defend, indemnify, and hold harmless HomeShark, its parent company, officers, directors,
        employees, agents, licensors, and service providers from and against any claims, liabilities, damages,
        judgments, awards, losses, costs, expenses, or fees (including reasonable attorneys' fees) arising out of or
        relating to:</p>
      <ul>
        <li><p>Your violation of these Terms.</p></li>
        <li><p>Your use of the Platform, including your use of the Contract Builder or any binding agreement generated
          thereby.</p></li>
        <li><p>Any real estate transaction you enter into through or facilitated by the Platform.</p></li>
        <li><p>Your violation of any third-party right, including any intellectual property right, privacy right, or MLS
          data licensing rule.</p></li>
        <li><p>Any inaccurate, incomplete, or misleading information you provide through the Platform.</p></li>
        <li><p>Your violation of any applicable law, regulation, or rule, including Georgia real estate law.</p></li>
      </ul>

      <!-- 13. Dispute Resolution -->
      <h2 class="tos-h1" id="dispute-resolution-arbitration">13. Dispute Resolution &amp; Arbitration</h2>

      <h3 class="tos-h2" id="informal-resolution">13.1 Informal Resolution</h3>
      <p>Before initiating any formal dispute resolution proceeding, you agree to first contact HomeShark at <a
        href="mailto:legal@homeshark.com">legal&#64;homeshark.com</a> and provide a written description of your dispute,
        the relief sought, and your contact information. HomeShark will attempt to resolve the dispute informally within
        thirty (30) days of receipt of your notice. If the dispute is not resolved within that period, either party may
        proceed to arbitration as set forth below.</p>

      <h3 class="tos-h2" id="binding-arbitration">13.2 Binding Arbitration</h3>
      <p><strong>Please read this section carefully. It affects your legal rights, including your right to file a
        lawsuit in court.</strong> Except for disputes that qualify for small claims court, you and HomeShark agree that
        any dispute, claim, or controversy arising out of or relating to these Terms or the Platform shall be resolved
        by final, binding arbitration administered by the American Arbitration Association (AAA) under its Consumer
        Arbitration Rules, rather than in court.</p>

      <div class="tos-callout-nomarket">
        <strong>Jury Trial &amp; Class Action Waiver</strong>
        <p>THE PARTIES HEREBY WAIVE ANY RIGHT TO A JURY TRIAL OR TO PARTICIPATE IN A CLASS ACTION WITH RESPECT TO ANY
          DISPUTE (Section 13.3, below).</p>
      </div>

      <p>The arbitration will be conducted in Fulton County, Georgia, unless the parties agree otherwise. The arbitrator
        shall have exclusive authority to resolve any Dispute, including any question regarding the existence, scope, or
        enforceability of this arbitration agreement. The arbitrator may award any relief available in a court of
        competent jurisdiction. Payment of all filing, administration, and arbitrator fees shall be governed by the AAA
        rules, unless otherwise required by applicable law. Each party shall bear its own attorneys' fees unless
        otherwise awarded by the arbitrator.</p>
      <p>If any portion of this arbitration agreement is found to be invalid or unenforceable, the remaining portions
        shall remain in full force and effect.</p>

      <h3 class="tos-h2" id="class-action-waiver">13.3 Class Action Waiver</h3>
      <p>YOU AND HOMESHARK AGREE THAT EACH MAY BRING CLAIMS AGAINST THE OTHER ONLY IN YOUR OR ITS INDIVIDUAL CAPACITY
        AND NOT AS A PLAINTIFF OR CLASS MEMBER IN ANY PURPORTED CLASS, REPRESENTATIVE, OR COLLECTIVE PROCEEDING.</p>
      <p>The arbitrator may not consolidate more than one person's claims and may not otherwise preside over any form of
        a representative or class proceeding. If this waiver is found unenforceable, then the entire arbitration
        provision in this Section 13 shall be null and void.</p>

      <h3 class="tos-h2" id="exceptions">13.4 Exceptions</h3>
      <p>Notwithstanding the foregoing agreement to arbitrate, either party may seek temporary, preliminary, or
        emergency injunctive or other equitable relief from a court of competent jurisdiction to prevent imminent and
        irreparable harm, including any actual or threatened misuse, misappropriation, or violation of intellectual
        property rights or confidential information, pending final resolution of the dispute through arbitration.</p>
      <p>Either party may also bring an individual action in a court of competent jurisdiction for the collection of
        amounts owed, provided that such claims do not involve issues otherwise subject to arbitration beyond the
        collection of such amounts.</p>
      <p>Nothing in this Section prevents HomeShark from pursuing collection of fees owed.</p>

      <h3 class="tos-h2" id="opt-out-arbitration">13.5 Right to Opt Out of Arbitration</h3>
      <p>You may opt out of the arbitration agreement contained in this Section by providing written notice to HomeShark
        at <a href="mailto:legal@homeshark.com">legal&#64;homeshark.com</a> within thirty (30) days of first accepting
        these Terms. Your notice must include your name, email address, and a clear statement that you wish to opt out
        of arbitration. If you opt out, you will retain the right to pursue disputes in court.</p>

      <!-- 14. Governing Law -->
      <h2 class="tos-h1" id="governing-law-jurisdiction">14. Governing Law &amp; Jurisdiction</h2>
      <p>These Terms, and any dispute, claim, or controversy arising out of or relating to these Terms or the Platform,
        shall be governed by and construed in accordance with the laws of the State of Georgia, without regard to its
        conflict of laws principles.</p>
      <p>To the extent any dispute is not subject to arbitration under Section 13, such dispute shall be brought
        exclusively in the state or federal courts located in Fulton County, Georgia, and the parties hereby consent to
        the personal jurisdiction and venue of such courts. Each party waives any objection based on improper venue or
        forum non conveniens.</p>

      <!-- 15. Termination -->
      <h2 class="tos-h1" id="termination">15. Termination of Account &amp; Access</h2>

      <h3 class="tos-h2" id="termination-by-homeshark">15.1 Termination by HomeShark</h3>
      <p>HomeShark reserves the right, in its sole discretion, to suspend or terminate your account and access to the
        Platform at any time and for any reason, with or without notice, including but not limited to:</p>
      <ul>
        <li><p>Violation of these Terms or any applicable law.</p></li>
        <li><p>Conduct that HomeShark determines, in its sole discretion, to be harmful to other users, third parties,
          or HomeShark's business interests.</p></li>
        <li><p>Non-payment of applicable fees.</p></li>
        <li><p>Inactivity for a period of twelve (12) consecutive months.</p></li>
        <li><p>Requests by law enforcement or government authorities.</p></li>
      </ul>

      <h3 class="tos-h2" id="termination-by-you">15.2 Termination by You</h3>
      <p>You may terminate your account at any time by contacting HomeShark at <a href="mailto:support@homeshark.com">support&#64;homeshark.com</a>
        or through your account settings. Termination does not entitle you to any refund of prepaid subscription fees,
        except as required by applicable law.</p>

      <h3 class="tos-h2" id="effect-of-termination">15.3 Effect of Termination</h3>
      <p>Upon termination of your account for any reason: (a) your right to access and use the Platform immediately
        ceases; (b) HomeShark may delete your account data in accordance with its data retention policies; (c) any
        pending contracts in the Contract Builder workflow will be handled in accordance with applicable Georgia real
        estate law and any executed Buyer Brokerage Agreement; and (d) all provisions of these Terms that by their
        nature should survive termination shall so survive, including without limitation Sections 8, 10, 11, 12, 13, 14,
        and 16.</p>

      <!-- 16. General Provisions -->
      <h2 class="tos-h1" id="general-provisions">16. General Provisions</h2>

      <h3 class="tos-h2" id="entire-agreement">16.1 Entire Agreement</h3>
      <p>These Terms, together with the Privacy Policy and any applicable Buyer Brokerage Agreement or
        transaction-specific addendum, constitute the entire agreement between you and HomeShark with respect to the
        Platform and supersede all prior and contemporaneous understandings, agreements, representations, and
        warranties.</p>

      <h3 class="tos-h2" id="severability">16.2 Severability</h3>
      <p>If any provision of these Terms is held by a court or arbitrator of competent jurisdiction to be invalid,
        illegal, or unenforceable, such provision shall be modified to the minimum extent necessary to make it
        enforceable. If such modification is not possible, the provision shall be severed, and the remaining provisions
        of these Terms shall remain in full force and effect.</p>

      <h3 class="tos-h2" id="waiver">16.3 Waiver</h3>
      <p>HomeShark's failure to enforce any right or provision of these Terms shall not be deemed a waiver of such right
        or provision. Any waiver of any provision of these Terms will be effective only if in writing and signed by an
        authorized representative of HomeShark.</p>

      <h3 class="tos-h2" id="assignment">16.4 Assignment</h3>
      <p>You may not assign or transfer your rights or obligations under these Terms without HomeShark's prior written
        consent. HomeShark may freely assign its rights and obligations under these Terms, including in connection with
        a merger, acquisition, sale of assets, or by operation of law.</p>

      <h3 class="tos-h2" id="force-majeure">16.5 Force Majeure</h3>
      <p>HomeShark shall not be liable for any failure or delay in the performance of its obligations under these Terms
        to the extent caused by circumstances beyond HomeShark's reasonable control, including acts of God, natural
        disasters, pandemics, war, terrorism, labor disputes, governmental actions, or failures of third-party service
        providers including MLS data providers.</p>

      <h3 class="tos-h2" id="notices">16.6 Notices</h3>
      <p>All notices, requests, and other communications from HomeShark to you shall be provided by email to the address
        associated with your account or by posting such notice on the Platform, and shall be deemed given upon sending
        (for email) or upon posting (for Platform notices).</p>
      <p>Notices to HomeShark must be sent by email to <a href="mailto:legal@homeshark.com">legal&#64;homeshark.com</a>
        or by certified mail, return receipt requested, to HomeShark's registered corporate address in the State of
        Georgia, and shall be deemed given upon receipt.</p>

      <h3 class="tos-h2" id="accessibility">16.7 Accessibility</h3>
      <p>HomeShark is committed to providing a Platform that is accessible to all users, including individuals with
        disabilities. If you encounter any accessibility barriers or have difficulty using the Platform, please contact
        us at <a href="mailto:support@homeshark.com">support&#64;homeshark.com</a>. We will make reasonable efforts to
        address your concerns and provide appropriate accommodations in accordance with applicable law.</p>

      <!-- 17. Contact -->
      <h2 class="tos-h1" id="contact-information">17. Contact Us — Terms Inquiries</h2>
      <p>If you have questions about these Terms of Service, please contact HomeShark at:</p>
      <div class="tos-table-wrap">
        <table>
          <thead>
          <tr>
            <th>Contact Method</th>
            <th>Details</th>
          </tr>
          </thead>
          <tbody>
          <tr>
            <td>Legal Inquiries</td>
            <td><a href="mailto:legal@homeshark.com">legal&#64;homeshark.com</a></td>
          </tr>
          <tr>
            <td>General Support</td>
            <td><a href="mailto:support@homeshark.com">support&#64;homeshark.com</a></td>
          </tr>
          <tr>
            <td>Website</td>
            <td>homeshark.com</td>
          </tr>
          <tr>
            <td>Company</td>
            <td>HomeShark, Inc.</td>
          </tr>
          <tr>
            <td>Jurisdiction</td>
            <td>State of Georgia, Fulton County, United States</td>
          </tr>
          <tr>
            <td>Governing Law</td>
            <td>Georgia</td>
          </tr>
          <tr>
            <td>GA Broker License</td>
            <td>272410</td>
          </tr>
          <tr>
            <td>Effective Date</td>
            <td>March 9, 2026</td>
          </tr>
          </tbody>
        </table>
      </div>

    </div><!-- /tos-content -->
  </main><!-- /tos-main -->
</div><!-- /tos-layout -->
